import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CircularProgress from '@mui/material/CircularProgress';
import { CircleCheck as CircleCheckIcon } from 'lucide-react';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { format } from 'date-fns';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';

export interface ApplicableEmail {
  id: string;
  label: string;
}

export type EmailStatus = 'pending' | 'sending' | 'success' | 'failed';

export interface EmailResult {
  status: EmailStatus;
  timestamp?: string;
  error?: string;
}

export interface EmailSession {
  selectedEmailIds: string[];
  /** HER report type ids included in the same dispatch / status poll as email events */
  selectedHerReportIds: string[];
  results: Record<string, EmailResult>;
  isProcessing: boolean;
  startTime?: string;
}

export interface EmailTriggerUser {
  id: string;
  displayName: string;
}

interface EmailTriggerDialogProps {
  open: boolean;
  onClose: () => void;
  user: EmailTriggerUser | null;
  session: EmailSession | null;
  onStart: (payload: { emailEventIds: string[]; herReportIds: string[] }) => void;
  onAcknowledge: () => void;
  emails: ApplicableEmail[];
  herReports: ApplicableEmail[];
  emailsLoading?: boolean;
  emailsError?: string | null;
}

export function EmailTriggerDialog({
  open,
  onClose,
  user,
  session,
  onStart,
  onAcknowledge,
  emails,
  herReports,
  emailsLoading = false,
  emailsError = null
}: EmailTriggerDialogProps) {
  const theme = useTheme();
  const [localSelected, setLocalSelected] = useState<string[]>([]);
  const [localSelectedHerReports, setLocalSelectedHerReports] = useState<string[]>([]);

  useEffect(() => {
    if (open && !session) {
      setLocalSelected([]);
      setLocalSelectedHerReports([]);
    }
  }, [open, session]);

  const handleToggle = (id: string, checked: boolean) => {
    setLocalSelected((prev) => (checked ? [...prev, id] : prev.filter((e) => e !== id)));
  };

  const handleHerReportToggle = (id: string, checked: boolean) => {
    setLocalSelectedHerReports((prev) => (checked ? [...prev, id] : prev.filter((e) => e !== id)));
  };

  const isProcessing = session?.isProcessing;
  const inDispatchFlow = Boolean(
    session && (session.isProcessing || Object.keys(session.results).length > 0)
  );
  const allFinished = Boolean(inDispatchFlow && !isProcessing);
  const totalSelectedCount = localSelected.length + localSelectedHerReports.length;

  const fallbackLabel = (id: string) => id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const renderStatusCard = (item: ApplicableEmail, result: EmailResult | undefined) => {
    const failed = result?.status === 'failed';
    const success = result?.status === 'success';

    return (
      <Box
        key={item.id}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: failed
            ? alpha(theme.palette.error.main, 0.5)
            : success
              ? alpha(theme.palette.success.main, 0.5)
              : 'divider',
          bgcolor: failed
            ? alpha(theme.palette.error.main, 0.12)
            : success
              ? '#f0fdf4'
              : 'action.hover',
          transition: 'all 0.2s'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
            <Box>
              <Typography variant="body2" fontWeight={700} color="text.primary">
                {item.label}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', opacity: 0.8 }}>
                {item.id}
              </Typography>
            </Box>
          </Box>

          {result && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
              {result.status === 'sending' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'primary.light',
                    bgcolor: 'background.paper'
                  }}
                >
                  <CircularProgress size={14} />
                  <Typography variant="caption" fontWeight={800} textTransform="uppercase" color="primary">
                    Sending...
                  </Typography>
                </Box>
              )}
              {result.status === 'pending' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover'
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" fontWeight={800} textTransform="uppercase" color="text.secondary">
                    Queued
                  </Typography>
                </Box>
              )}
              {result.status === 'success' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'success.light',
                    bgcolor: alpha(theme.palette.success.main, 0.12)
                  }}
                >
                  <CircleCheckIcon size={14} color={theme.palette.success.main} />
                  <Typography variant="caption" fontWeight={800} textTransform="uppercase" color="success.dark">
                    Sent successfully
                  </Typography>
                </Box>
              )}
              {result.status === 'failed' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.error.main, 0.5),
                    bgcolor: alpha(theme.palette.error.main, 0.15)
                  }}
                >
                  <CancelIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
                  <Typography variant="caption" fontWeight={800} textTransform="uppercase" sx={{ color: theme.palette.error.main }}>
                    Failed
                  </Typography>
                </Box>
              )}
              {result.timestamp && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.65rem' }}>
                  Finished at {format(new Date(result.timestamp), 'HH:mm:ss')}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {result?.status === 'failed' && result.error && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: alpha(theme.palette.error.main, 0.3),
              bgcolor: alpha(theme.palette.error.main, 0.08)
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 16, color: theme.palette.error.main, mt: 0.25, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: theme.palette.error.main, lineHeight: 1.5 }}>
              {result.error?.includes('Delivery Team')
                ? <>
                    {result.error.split('Delivery Team')[0]}
                    <strong>Delivery Team</strong>
                    {result.error.split('Delivery Team')[1]}
                  </>
                : result.error
              }
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <MailOutlineIcon color="primary" sx={{ fontSize: 28 }} />
        Trigger Test Emails
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: isProcessing || allFinished ? 0.5 : 2 }}>
          {allFinished
            ? 'Review the results of your previous dispatch session.'
            : isProcessing
              ? 'Dispatch in progress. You can close this window and return later.'
              : `Select templates to send to ${user?.displayName ?? 'this user'}.`}
        </Typography>
        {inDispatchFlow && user?.displayName && (
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            {user.displayName}
          </Typography>
        )}

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 1.5,
            fontWeight: 900,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'text.secondary'
          }}
        >
          {inDispatchFlow ? 'Dispatch Status' : 'Applicable Email Events'}
        </Typography>

        {isProcessing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: '1px solid',
              borderColor: 'info.light'
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
            <Typography variant="body2" color="info.dark" fontWeight={500}>
              This will take some time
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {emailsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2" color="text.secondary">Loading email events...</Typography>
            </Box>
          ) : emailsError ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'error.light', bgcolor: alpha(theme.palette.error.main, 0.06) }}>
              <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
              <Typography variant="body2" color="error.dark">{emailsError}</Typography>
            </Box>
          ) : inDispatchFlow && session ? (
            <>
              {session.selectedEmailIds.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1,
                      fontWeight: 800,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'text.secondary'
                    }}
                  >
                    Email events
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: session.selectedHerReportIds.length > 0 ? 2 : 0 }}>
                    {session.selectedEmailIds.map((id) => {
                      const item = emails.find((e) => e.id === id) ?? { id, label: fallbackLabel(id) };
                      return renderStatusCard(item, session.results[id]);
                    })}
                  </Box>
                </>
              )}
              {session.selectedHerReportIds.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1,
                      fontWeight: 800,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'text.secondary'
                    }}
                  >
                    HER reports
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {session.selectedHerReportIds.map((id) => {
                      const item = herReports.find((r) => r.id === id) ?? { id, label: fallbackLabel(id) };
                      return renderStatusCard(item, session.results[id]);
                    })}
                  </Box>
                </>
              )}
            </>
          ) : emails.length === 0 && herReports.length === 0 && !inDispatchFlow ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No email events or HER reports available for this user.</Typography>
            </Box>
          ) : (
            emails.map((email) => {
              const isSelected = localSelected.includes(email.id);

              return (
                <Box
                  key={email.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    transition: 'all 0.2s',
                    ...(isSelected && {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderColor: alpha(theme.palette.primary.main, 0.4)
                    })
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={(_, checked) => handleToggle(email.id, checked)}
                        disabled={isProcessing}
                        sx={{ p: 0 }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={700} color="text.primary">
                          {email.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', opacity: 0.8 }}>
                          {email.id}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {!inDispatchFlow && !emailsLoading && !emailsError && (
          <>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 3,
                mb: 1.5,
                fontWeight: 900,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'text.secondary'
              }}
            >
              Applicable HER Reports
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {herReports.length === 0 ? (
                <Box sx={{ py: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No HER reports available for this user.</Typography>
                </Box>
              ) : herReports.map((report) => {
                const isSelected = localSelectedHerReports.includes(report.id);

                return (
                  <Box
                    key={report.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.4)
                        : 'divider',
                      bgcolor: isSelected
                        ? alpha(theme.palette.primary.main, 0.1)
                        : 'action.hover',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={(_, checked) => handleHerReportToggle(report.id, checked)}
                        disabled={isProcessing}
                        sx={{ p: 0 }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={700} color="text.primary">
                          {report.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', opacity: 0.8 }}>
                          {report.id}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: allFinished ? 'space-between' : 'flex-end',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        {allFinished ? (
          <>
            <PermissionGate require={UI_PAGES.CUSTOMER_DATA_GENERATOR.ctas.reset_email_session.id} mode="disable">
              <Button
                onClick={onAcknowledge}
                color="inherit"
                startIcon={<RefreshIcon />}
                sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}
              >
                Reset Session
              </Button>
            </PermissionGate>
            <Button variant="contained" onClick={onClose} sx={{ fontWeight: 700, textTransform: 'uppercase', px: 3 }}>
              Okay
            </Button>
          </>
        ) : isProcessing ? (
          <Button
            variant="contained"
            fullWidth
            onClick={onClose}
            startIcon={<CircularProgress size={18} color="inherit" />}
            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
          >
            Continue in Background
          </Button>
        ) : (
          <>
            <Button color="inherit" onClick={onClose} sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              Cancel
            </Button>
            <PermissionGate require={UI_PAGES.CUSTOMER_DATA_GENERATOR.ctas.trigger_email_run.id} mode="disable">
              <Button
                variant="contained"
                onClick={() => onStart({ emailEventIds: localSelected, herReportIds: localSelectedHerReports })}
                disabled={totalSelectedCount === 0 || emailsLoading || (emails.length === 0 && herReports.length === 0)}
                sx={{ fontWeight: 700, textTransform: 'uppercase', px: 2 }}
              >
                Send{totalSelectedCount > 0 ? ` (${totalSelectedCount})` : ''}
              </Button>
            </PermissionGate>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
